<template>
  <div class="base-header">
    <div class="base-header__content">
      <tertiary-button
        class="base-header__back-button"
        variant="inverted"
        @click="goBack"
      >
        Tilbake
      </tertiary-button>
      <div class="base-header__offer-details">
        <img :src="`/images/document-white.svg`" alt="Document" />
        <div>
          <div class="base-header__offer-recipient">
            {{ offerTermCapitalized }} til {{ offerRecipient }}
          </div>
          <div v-if="lastSaved" class="base-header__offer-last-saved">
            Sist lagret kl. {{ lastSaved }}
          </div>
        </div>
        <Tag
          v-if="currentStatus"
          :value="currentStatus.label"
          :severity="currentStatus.severity"
          class="base-header__status-tag"
        />
      </div>
      <div class="base-header__right-content">
        <slot name="right-content" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import TertiaryButton from "@/components/Buttons/TertiaryButton.vue";
import Tag from "primevue/tag";
import { useOfferTerminology } from "@/composables/useOfferTerminology";
import { OfferStatus } from "@/custom-types/GeneralTypes";
import { computed } from "vue";

const props = defineProps({
  offerRecipient: {
    type: String,
    default: "",
  },
  lastSaved: {
    type: [String, null],
    default: null,
  },
  offerStatus: {
    type: String as () => OfferStatus | null,
    default: null,
  },
});

const emit = defineEmits(["go-back"]);

const { offerTermCapitalized } = useOfferTerminology();

// Status mapping for Norwegian terminology with appropriate severities
const statusMapping = {
  [OfferStatus.DRAFT]: { label: "Utkast", severity: "secondary" },
  [OfferStatus.OFFERED]: { label: "Tilbudt", severity: "info" },
  [OfferStatus.WITHDRAWN]: { label: "Avbrutt", severity: "warn" },
  [OfferStatus.ACCEPTED]: { label: "Akseptert", severity: "success" },
  [OfferStatus.REJECTED]: { label: "Avvist", severity: "danger" },
  [OfferStatus.COMPLETED]: { label: "Fullført", severity: "success" },
  [OfferStatus.LOST]: { label: "Tapt", severity: "danger" },
};

const currentStatus = computed(() => {
  if (!props.offerStatus) {
    return null;
  }

  // Normalize the status to capitalized format to handle case differences
  const normalizedStatus = (props.offerStatus.charAt(0).toUpperCase() +
    props.offerStatus.slice(1).toLowerCase()) as OfferStatus;

  if (!statusMapping[normalizedStatus]) {
    return null;
  }

  return statusMapping[normalizedStatus];
});

function goBack() {
  emit("go-back");
}
</script>

<style lang="scss" scoped>
.base-header {
  padding-top: 4rem;

  &__content {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 4rem;
    z-index: 1000;
    background-color: #611f69;
    color: $color-white;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 4rem;
  }

  &__back-button {
    padding: 0.625rem 1rem;
  }

  &__offer-details {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    text-align: left;
    font-weight: 400;
  }

  &__offer-recipient {
    font-size: $font-size-sm;
  }

  &__offer-last-saved {
    font-size: $font-size-xs;
    line-height: 1.18;
  }

  &__status-tag {
    margin-left: 1rem;
    flex-shrink: 0;
  }
}
</style>
